import React, {
  useRef,
  useState,
} from "react";

import {
  Bank,
  CheckCircle,
  Circle,
  FileText,
  UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { gmao } from "@/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/api";


function errorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail;

  if (detail == null) {
    return fallback;
  }

  const message =
    formatApiError(detail);

  return (
    message
    && message !== "Une erreur est survenue."
      ? message
      : fallback
  );
}


function ValidationRow({
  label,
  ok,
  required,
}) {
  const Icon = ok
    ? CheckCircle
    : Circle;

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 items-center gap-2 text-sm text-[var(--do-text)]">
        <Icon
          size={17}
          weight={ok ? "fill" : "regular"}
          className={
            ok
              ? "text-[var(--do-success)]"
              : "text-[var(--do-muted)]"
          }
        />

        <span className="truncate">
          {label}
        </span>
      </span>

      <span className="shrink-0 text-xs font-bold text-[var(--do-muted)]">
        {ok
          ? "OK"
          : required
            ? "À compléter"
            : "Optionnel"}
      </span>
    </div>
  );
}


export default function InstallationDeliveryValidationCard({
  installationId,
  approval = {},
  validation = {},
  legacy = false,
  disabled = false,
  onChanged,
}) {
  const inputRef = useRef(null);

  const [busy, setBusy] =
    useState(false);

  const received = Boolean(
    approval.received
  );

  const document =
    approval.document || {};

  const updateApproval =
    async (checked) => {
      setBusy(true);

      try {
        await gmao
          .updateInstallationBankDeliveryApproval(
            installationId,
            {
              received: checked,
            }
          );

        toast.success(
          checked
            ? "Accord banque enregistré."
            : "Accord banque décoché."
        );

        await onChanged?.();
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible de modifier l’accord banque."
          )
        );
      } finally {
        setBusy(false);
      }
    };

  const uploadDocument =
    async (event) => {
      const file =
        event.target.files?.[0];

      event.target.value = "";

      if (!file) {
        return;
      }

      setBusy(true);

      try {
        await gmao
          .uploadInstallationBankDeliveryApprovalDocument(
            installationId,
            file
          );

        toast.success(
          "Document bancaire ajouté."
        );

        await onChanged?.();
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible d’ajouter le document bancaire."
          )
        );
      } finally {
        setBusy(false);
      }
    };

  const openDocument =
    async () => {
      if (!document.id) {
        return;
      }

      setBusy(true);

      try {
        const blob =
          await gmao
            .getInstallationBankDeliveryApprovalDocumentBlob(
              installationId
            );

        const url =
          URL.createObjectURL(blob);

        const link =
          window.document.createElement(
            "a"
          );

        link.href = url;
        link.target = "_blank";
        link.rel =
          "noopener noreferrer";

        window.document.body
          .appendChild(link);

        link.click();
        link.remove();

        window.setTimeout(
          () =>
            URL.revokeObjectURL(
              url
            ),
          1000
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible d’ouvrir le document bancaire."
          )
        );
      } finally {
        setBusy(false);
      }
    };

  return (
    <Card
      variant="acta"
      className="p-5"
      data-testid="installation-delivery-validation"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-surface-soft)] text-[var(--do-accent)]">
          <Bank size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="label-eyebrow">
            Validation livraison
          </div>

          <h2 className="mt-1 font-heading text-lg font-black text-[var(--do-text)]">
            Dossier de livraison
          </h2>

          <p className="mt-1 text-sm text-[var(--do-muted)]">
            Vérifiez les informations liées avant la livraison.
          </p>
        </div>
      </div>

      <div className="mt-5 divide-y divide-[var(--do-border)]">
        {(validation.checks || [])
          .filter(
            (check) =>
              check.key
              !== "bank_approval"
          )
          .map((check) => (
            <ValidationRow
              key={check.key}
              label={check.label}
              ok={check.ok}
              required={
                check.required
              }
            />
          ))}
      </div>

      <div className="mt-5 border-t border-[var(--do-border)] pt-5">
        <label className="flex items-start gap-3">
          <Checkbox
            
            checked={received}
            disabled={
              disabled
              || busy
              || legacy
            }
            onCheckedChange={(checked) => updateApproval(checked === true)}
            className="mt-1"
            data-testid="installation-bank-approval-checkbox"
          />

          <span>
            <strong className="block text-sm font-heading text-[var(--do-text)]">
              Accord de livraison de la banque reçu
            </strong>

            <span className="mt-1 block text-xs text-[var(--do-muted)]">
              Le document justificatif est facultatif.
            </span>
          </span>
        </label>

        {!legacy && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={
                uploadDocument
              }
              data-testid="installation-bank-approval-file"
            />

            {document.id ? (
              <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3">
                <div className="flex items-center gap-2">
                  <FileText
                    size={18}
                    className="shrink-0 text-[var(--do-accent)]"
                  />

                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--do-text)]">
                    {document.name
                    || "Accord bancaire"}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="actaSecondary"
                    disabled={busy}
                    onClick={
                      openDocument
                    }
                  >
                    Voir
                  </Button>

                  <Button
                    type="button"
                    variant="actaSecondary"
                    disabled={busy}
                    onClick={() =>
                      inputRef.current
                        ?.click()
                    }
                  >
                    Remplacer
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="actaSecondary"
                className="mt-4 w-full"
                disabled={
                  disabled
                  || busy
                }
                onClick={() =>
                  inputRef.current
                    ?.click()
                }
              >
                <UploadSimple
                  size={17}
                />
                Ajouter le document
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
